import { useEffect, useState } from 'react'

interface TopBar {
  mode: 'strip' | 'banner'
  color?: string
  height?: string
  text?: string
  buttonText?: string
  buttonUrl?: string
  hidden?: boolean
  textColor?: string
  textSize?: string
  textWeight?: string
  textTransform?: string
  letterSpacing?: string
  gap?: string
}

const API_BASE = import.meta.env.VITE_BACKEND_URL || 'http://localhost:2001'
const BUILD_ENV = import.meta.env.MODE
const PREVIEW_KEY = 'app-elements-preview-key'
const ENV_OVERRIDE_KEY = 'app-elements-preview-env'
const isDev = BUILD_ENV !== 'production'

const VALID_ENVS = new Set(['production', 'development'])

// In dev/staging, allow overriding the env via ?_topBarEnv=production or
// localStorage so a developer can preview what production will render
// without redeploying.
function resolveEnv(): string {
  if (BUILD_ENV === 'production') return 'production'
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('_topBarEnv')
    if (fromQuery && VALID_ENVS.has(fromQuery)) return fromQuery
    const fromStorage = localStorage.getItem(ENV_OVERRIDE_KEY)
    if (fromStorage && VALID_ENVS.has(fromStorage)) return fromStorage
  } catch {
    /* ignore */
  }
  return BUILD_ENV
}

export function HubTopBar() {
  const [topBar, setTopBar] = useState<TopBar | null>(null)
  const [previewApp, setPreviewApp] = useState<string>(
    () => (isDev && localStorage.getItem(PREVIEW_KEY)) || 'hub'
  )
  const [env, setEnv] = useState<string>(() => resolveEnv())
  // Unsaved edits coming from the admin tab. Lives in component state only,
  // so a hard refresh wipes it back to whatever's in the DB.
  const [draft, setDraft] = useState<{
    app_key: string
    env: string
    element: string
    config: Record<string, any>
  } | null>(null)

  useEffect(() => {
    let cancelled = false

    const fetchBar = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/elements?app=${previewApp}&env=${env}`)
        const { elements } = await res.json()
        if (!cancelled) setTopBar(elements?.topBar || null)
      } catch {
        if (!cancelled) setTopBar(null)
      }
    }

    fetchBar()

    const onChanged = () => {
      // Save was successful — the DB now matches our draft, drop it so the
      // refetched API result becomes the source of truth.
      setDraft(null)
      fetchBar()
    }
    const onPreview = (e: Event) => {
      const detail = (e as CustomEvent<{ appKey?: string; env?: string }>).detail
      if (detail?.appKey) setPreviewApp(detail.appKey)
      if (detail?.env && VALID_ENVS.has(detail.env)) setEnv(detail.env)
    }
    const onDraft = (e: Event) => {
      const detail = (e as CustomEvent<{
        app_key: string
        env: string
        element: string
        config: Record<string, any>
      }>).detail
      if (!detail) return
      setDraft(detail)
    }
    window.addEventListener('app-elements-changed', onChanged)
    window.addEventListener('app-elements-preview-changed', onPreview)
    window.addEventListener('app-elements-draft-changed', onDraft)
    return () => {
      cancelled = true
      window.removeEventListener('app-elements-changed', onChanged)
      window.removeEventListener('app-elements-preview-changed', onPreview)
      window.removeEventListener('app-elements-draft-changed', onDraft)
    }
  }, [previewApp, env])

  // Merge unsaved draft on top of the fetched config when scope matches.
  // Global drafts apply to any previewed app; per-app drafts only apply to
  // their own app.
  const effective: TopBar | null =
    topBar &&
    draft &&
    draft.element === 'topBar' &&
    draft.env === env &&
    (draft.app_key === 'global' || draft.app_key === previewApp)
      ? { ...topBar, ...draft.config }
      : topBar

  useEffect(() => {
    const height = effective && !effective.hidden ? effective.height || '0px' : '0px'
    // Set a CSS var on <html> so the app layout can shrink by exactly the bar
    // height instead of letting body grow taller than the viewport (which would
    // produce a second scrollbar alongside the in-app scroll container).
    document.documentElement.style.setProperty('--top-bar-height', height)
    return () => {
      document.documentElement.style.setProperty('--top-bar-height', '0px')
    }
  }, [effective])

  if (!effective || effective.hidden) return null

  const textColor = effective.textColor || 'white'
  const textSize = effective.textSize || '13px'
  const textWeight = effective.textWeight || '600'

  const baseStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: effective.height,
    background: effective.color,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: textColor,
    fontWeight: textWeight as React.CSSProperties['fontWeight'],
    gap: effective.gap || '12px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontSize: textSize,
    textTransform: effective.textTransform as React.CSSProperties['textTransform'],
    letterSpacing: effective.letterSpacing,
  }

  // Mode comes from the backend (strip in prod, banner otherwise) — drafts
  // change underlying fields, not mode. Falls back to banner if the draft
  // exists before the first fetch lands.
  const mode = effective.mode || 'banner'

  if (mode === 'strip') {
    return <div style={baseStyle} />
  }

  const content = (
    <>
      {effective.text && <span>{effective.text}</span>}
      {effective.buttonUrl && effective.buttonText && (
        <span
          style={{
            color: textColor,
            fontSize: textSize,
            fontWeight: 500,
            textDecoration: 'underline',
          }}
        >
          {effective.buttonText}
        </span>
      )}
    </>
  )

  if (effective.buttonUrl) {
    return (
      <a
        href={effective.buttonUrl}
        style={{ ...baseStyle, textDecoration: 'none', cursor: 'pointer' }}
      >
        {content}
      </a>
    )
  }

  return <div style={baseStyle}>{content}</div>
}
